import React from 'react';
import {
  Table, TableHead, TableRow, TableCell, TableBody, Chip,
} from '@mui/material';
import { Warning as WarningIcon, Error as ErrorIcon } from '@mui/icons-material';
import { ModalWrapper } from './ModalWrapper';
import { Task } from '../../types/task';
import { formatDateTime } from '../../utils/dateUtils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
  /** Opens the issue's action (review popup, summary, meeting) — see useTaskActions. */
  onOpenTask?: (task: Task) => void;
}

const SEVERITY_COLORS: Record<string, 'error' | 'warning'> = {
  ERROR: 'error',
  WARNING: 'warning',
};

const formatTypeLabel = (type: string) =>
  type.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

const PatientIssuesModal: React.FC<Props> = ({ isOpen, onClose, tasks, onOpenTask }) => {
  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} title="Issues" width="max-w-4xl">
      <div className="px-6 pb-6">
        {tasks.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No issues</p>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Issue</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Owner</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Meeting</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map(task => (
                <TableRow key={task.logical_id}>
                  <TableCell>
                    <Chip
                      icon={task.severity === 'ERROR' ? <ErrorIcon /> : <WarningIcon />}
                      label={formatTypeLabel(task.type)}
                      color={SEVERITY_COLORS[task.severity] || 'default'}
                      size="small"
                      onClick={onOpenTask ? () => onOpenTask(task) : undefined}
                      sx={onOpenTask ? { cursor: 'pointer' } : undefined}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {task.owner_name || 'Secretary'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(task.date)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {task.meeting_scheduled_at
                      ? `${task.meeting_type_name || ''} ${formatDateTime(task.meeting_scheduled_at)}`.trim()
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </ModalWrapper>
  );
};

export default PatientIssuesModal;
